import { Button } from '@/components/UI/Common/Admin/Button';
import { Checkbox } from '@/components/UI/Common/Admin/Checkbox';
import { LoadingSpinner } from '@/components/UI/Common/Admin/LoadingSpinner';
import { PageLayout } from '@/components/UI/Layouts/Admin/PageLayout';
import Table from '@/components/UI/Table/Table';
import { ROUTES } from '@/config/routes';
import { useDeleteMany } from '@/queries/admin';
import { IApplicationLink } from '@/types/appliction';
import { TableColumn } from '@/types/table';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import { Filters } from '../components/Filters';
import { Pagination } from '@/components/UI/Pagination/Pagination';
import { useResourceList } from '@/hooks/useResourceList';

export const ApplicationLinksPage = () => {
  const {
    data: applicationLinks,
    isLoading,
    reset,
    handleFilterChange,
    handleSortChange,
    handlePageChange,
    handleLimitChange,
    filter,
    page,
    sortOrder,
    totalPages,
    totalItems,
    limit,
    sortBy,
  } = useResourceList('application-links');
  const { mutateAsync: deleteApplicationLinks } = useDeleteMany('application-links');
  const navigate = useNavigate();
  const [idsToDelete, setIdsToDelete] = useState<string[]>([]);

  const toggleDelete = () => {
    if (idsToDelete?.length === applicationLinks?.data.length) {
      setIdsToDelete([]);
    } else {
      setIdsToDelete(applicationLinks?.data.map((link) => link.id) || []);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteApplicationLinks({ ids: idsToDelete });
      toast.success('Группы успешно удалены');
      setIdsToDelete([]);
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Произошла ошибка при удалении группы');
    }
  };

  const renderCheckBox = (_val: unknown, row: IApplicationLink) => (
    <Checkbox
      className="prevent-row-click"
      checked={idsToDelete?.includes(row.id)}
      onChange={() => {
        if (idsToDelete?.includes(row.id)) {
          setIdsToDelete(idsToDelete.filter((id) => id !== row.id));
        } else {
          setIdsToDelete([...idsToDelete, row.id]);
        }
      }}
    />
  );

  const columns: TableColumn<IApplicationLink>[] = useMemo(
    () => [
      {
        key: '',
        title: (
          <Checkbox
            onClick={toggleDelete}
            onChange={() => {}}
            checked={idsToDelete?.length === applicationLinks?.data.length}
          />
        ),
        render: renderCheckBox,
      },
      {
        key: 'id',
        title: 'ID',
        sortable: true,
        sortBy: sortBy,
        sortOrder: sortOrder,
        onColumnHeaderClick: handleSortChange,
      },
      {
        key: 'title',
        title: 'Название',
        sortable: true,
        sortBy: sortBy,
        sortOrder: sortOrder,
        onColumnHeaderClick: handleSortChange,
      },
      { key: 'link', title: 'Ссылка' },
      { key: 'description', title: 'Описание' },
      {
        key: 'sort',
        title: 'Порядок',
        sortable: true,
        sortBy: sortBy,
        sortOrder: sortOrder,
        onColumnHeaderClick: handleSortChange,
      },
      {
        key: 'createdAt',
        title: 'Создана',
        sortable: true,
        sortBy: sortBy,
        sortOrder: sortOrder,
        onColumnHeaderClick: handleSortChange,
        render: (val: unknown) =>
          new Date(val as string).toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }),
      },
      {
        key: 'updatedAt',
        title: 'Обновлена',
        sortable: true,
        sortBy: sortBy,
        sortOrder: sortOrder,
        onColumnHeaderClick: handleSortChange,
        render: (val: unknown) =>
          new Date(val as string).toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }),
      },
    ],
    [applicationLinks?.data.length, idsToDelete.length, sortBy, sortOrder],
  );

  if (isLoading || !applicationLinks) {
    return <LoadingSpinner className="h-full" text="Загрузка..." />;
  }

  return (
    <PageLayout
      actions={
        <div className="flex gap-4">
          {idsToDelete.length > 0 && (
            <Button onClick={handleDelete} size="md" variant="danger">
              Удалить
            </Button>
          )}
          <Link to={ROUTES.ADMIN.CREATE('application-links')}>
            <Button size="md" variant="success">
              Создать
            </Button>
          </Link>
        </div>
      }
      title="Ссылки на документы"
    >
      <Filters value={filter} onChange={handleFilterChange} onReset={reset} />

      {applicationLinks.data.length > 0 && (
        <div className="overflow-x-auto">
          <Table
            data={applicationLinks!.data}
            columns={columns}
            onRowClick={(row) => navigate(ROUTES.ADMIN.EDIT('application-links', row.id))}
          />
        </div>
      )}

      <Pagination
        className="mt-10"
        onItemsPerPageChange={handleLimitChange}
        currentPage={page}
        totalItems={totalItems}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        itemsPerPage={limit}
      />
    </PageLayout>
  );
};
